

export enum CalculatorMode {
  PAYOFF = 'payoff',
  BUY_NEW = 'buy_new',
  COST_ESTIMATOR = 'cost_estimator',
  PMI_REMOVAL = 'pmi_removal',
}

// --- Shared Types ---
export interface LumpSum {
  amount: number;
  yearsFromNow: number;
}

export enum FilingStatus {
  SINGLE = 'Single',
  MARRIED_FILING_JOINTLY = 'Married Filing Jointly',
  MARRIED_FILING_SEPARATELY = 'Married Filing Separately',
  HEAD_OF_HOUSEHOLD = 'Head of Household',
}

// --- Pay Off Mortgage Early Types ---

export interface PayoffInputs {
  originalLoanAmount: number;
  interestRate: number;
  originationDate: string;
  loanTerm: number; // in years
  homeValue: number;
  appreciation: number;
  extraPayment: number; // monthly
  initialReturnRate: number;
  initialReturnPeriod: number; // in years
  longTermReturnRate: number;
  taxRateOnGains: number;
  filingStatus: FilingStatus;
  marginalTaxRate: number;
  otherItemizedDeductions: number;
  lumpSums: LumpSum[];
  // ARM Inputs
  isARM: boolean;
  armFixedTerm: number; // The initial fixed period in years (e.g. 5 for a 5/1 ARM)
  armSubsequentRate: number; // The expected rate after the fixed period
}

export interface PayoffMonthlyData {
  month: number;
  year: number;
  // Payoff scenario
  payoffBalance: number;
  payoffInvestBalance: number;
  payoffNetWorth: number;
  payoffTaxSavingsThisYear: number;
  payoffHomeEquity: number;
  // Invest scenario
  investBalance: number;
  originalBalance: number; // This is the mortgage balance for invest scenario
  investNetWorth: number;
  taxSavingsThisYear: number;
  investHomeEquity: number;
}

export interface AfterTaxDetails {
    finalValue: number;
    finalGains: number;
    taxes: number;
    finalValueAfterTax: number;
}

export interface PayoffCalculationResults {
  summary: {
    finalPayoffNetWorth: number;
    finalInvestNetWorth: number;
    payoffInvestmentDetails: AfterTaxDetails;
    investInvestmentDetails: AfterTaxDetails;
    interestSaved: number;
    newPayoffDate: string;
    yearsShortened: number;
    totalTaxSavingsInvested: number;
    itemizationInfo: {
        standardDeduction: number;
        itemizedYears: number[];
    };
  };
  monthlyData: PayoffMonthlyData[];
}


// --- Buy New Home (Finance vs Cash) Types ---

export enum CreditScoreTier {
  EXCELLENT = 'Excellent (760+)',
  GOOD = 'Good (700-759)',
  FAIR = 'Fair (660-699)',
  POOR = 'Poor (<660)',
}

export interface HomeInputs {
  purchasePrice: number;
  downPayment: number; // Percentage
  interestRate: number;
  loanTerm: number;
  startDate: string;
  closingCosts: number; // One-time cost
  insuranceSavings: number; // Annual savings for cash purchase
  appreciation: number;
  // ARM Inputs
  isARM: boolean;
  armFixedTerm: number;
  armSubsequentRate: number;
  lumpSums: LumpSum[];
}

export interface TaxInputs {
  filingStatus: FilingStatus;
  marginalTaxRate: number;
  otherItemizedDeductions: number;
}

export interface InvestmentInputs {
  initialReturnRate: number;
  initialReturnPeriod: number; // in years
  longTermReturnRate: number;
  taxRateOnGains: number;
}

export interface BuyNewHomeInputs {
  home: HomeInputs;
  tax: TaxInputs;
  investment: InvestmentInputs;
}

export interface BuyNewHomeMonthlyData {
  month: number;
  date: string;
  year: number;

  // Finance scenario
  homeValue: number;
  mortgageBalance: number;
  homeEquity: number;
  investedBalanceFinance: number;
  financeScenarioNetWorth: number;
  taxSavingsThisYear: number;

  // Pay Cash scenario
  investedBalancePayCash: number;
  payCashScenarioNetWorth: number;
}

export interface BuyNewHomeCalculationResults {
  summary: {
    loanAmount: number;
    downPaymentAmount: number;
    financeScenarioFinalNetWorth: number;
    payCashScenarioFinalNetWorth: number;
    totalInterestPaid: number;
    totalPrincipalPaid: number;
    itemizationInfo: {
      standardDeduction: number;
      itemizedYears: number[];
    };
  };
  monthlyData: BuyNewHomeMonthlyData[];
}

export interface MortgageRate {
  type: string;
  term: number;
  rate: number;
  history?: {
    labels: string[];
    data: number[];
  };
}

// --- PMI Removal Types ---

export enum PMIRemovalRule {
  AUTOMATIC_78 = 'Automatic (78% LTV)',
  REQUEST_80 = 'Request (80% LTV)',
  LIFE_OF_LOAN = 'Life of Loan (FHA)',
}

export interface PMIInputs {
  currentBalance: number;
  originalHomeValue: number; // LTV is usually based on original value
  interestRate: number;
  originationDate: string; // Changed from remainingTerm
  loanTerm: number;        // Added
  monthlyPMI: number;
  removalRule: PMIRemovalRule;
  extraPayment: number;
  investmentReturnRate: number;
}

export interface PMIStrategyResult {
  strategyName: string;
  finalNetWorth: number;
  totalPMIPaid: number;
  monthsUntilPMIRemoved: number;
  totalInterestPaid: number;
}

export interface PMIMonthlyData {
    month: number;
    year: number;
    isPMIRemoved: {
        baseline: boolean;
        sprint: boolean;
        payoff: boolean;
    };
    balances: {
        baseline: number;
        sprint: number;
        payoff: number;
    };
    investments: {
        baseline: number;
        sprint: number;
        payoff: number;
    };
    netWorth: {
        baseline: number;
        sprint: number;
        payoff: number;
    };
}

export interface PMICalculationResults {
  strategies: {
    baseline: PMIStrategyResult; // Just Pay Min, Invest Extra
    sprint: PMIStrategyResult;   // Pay Extra until PMI gone, then Invest
    payoff: PMIStrategyResult;   // Pay Extra until Mortgage gone
  };
  monthlyData: PMIMonthlyData[]; 
}